import type { HttpOptions } from '@apollo/client'
import { ApolloClient, ApolloLink, HttpLink, from } from '@apollo/client'
import type { ErrorResponse } from '@apollo/client/link/error'
import { onError } from '@apollo/client/link/error'
import {
  AppError,
  parseApolloError,
} from '@twistopayments/design-system/components/molecules/error-boundary'
import { gitRevision, isDev } from '@twistopayments/design-system/environment'
import { getFieldResponseError } from '@twistopayments/design-system/form'
import { isNil } from 'ramda'

import { networkStatusCode } from '@twisto/api-common'
import { debug, logger } from '@twisto/utils'

import { cache } from './cache'

export const globalErrorsHandler = ({
  error,
  setError,
  showDefaultError,
}: {
  error: ErrorResponse
  setError: (error: AppError) => void
  showDefaultError: () => void
}) => {
  const { graphQLErrors, response, networkError } = error

  if (graphQLErrors) {
    // handle graphql errors by displaying default error modal with generic message
    // More about graphql errors here: https://www.apollographql.com/docs/react/data/error-handling/

    // If BE sends 403, "permission denied" error, we handle it separately...
    // ...by displaying error modal and redirecting to login page
    if (
      graphQLErrors[0]?.extensions.code === networkStatusCode.permissionDenied
    ) {
      window.location.href = '/app/auth/'

      return showDefaultError()
    }

    // otherwise display default error modal
    // capture the errror in Sentry
    // and on dev, log the error to console
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn(`
          GLOBAL ERROR!:
          ==============
          ${debug(response)}
          ==============
          ${debug(graphQLErrors)}
          ==============\n
        `)
    } else {
      showDefaultError()

      logger.logError('GraphQL error', {
        response: debug(response),
        error: debug(graphQLErrors),
      })
    }
  }

  if (networkError) {
    // check that the error isn't an intentional request abortion
    const context = error.operation.getContext()
    if (
      isNil(context.fetchOptions?.signal) ||
      error.networkError?.name !== 'AbortError'
    ) {
      setError(
        new AppError(networkError.message, {
          ...parseApolloError(error),
          payload: networkError,
        })
      )
    }

    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn(`[Network error]: ${debug(networkError)}`)
    } else if (
      'statusCode' in networkError &&
      networkError.statusCode >= networkStatusCode.internalServerError
    ) {
      logger.logError('Network error', {
        networkError,
      })
    }
  }
}

export const setupFlowApi = ({
  config,
  getHeaders,
  setError,
  showErrorWithContinue,
  showDefaultError,
}: {
  config: HttpOptions
  getHeaders: () => Record<string, string>
  setError: (error: AppError) => void
  showErrorWithContinue: (message: string) => void
  showDefaultError: () => void
}) => {
  const nonFieldErrorHandlerLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: getHeaders(),
    })

    return forward(operation).map((result) => {
      if (!result.data) {
        return result
      }

      const context = operation.getContext()
      const overrideNonFieldErrorHandler =
        (context.overrideNonFieldErrorHandler ?? false) as boolean

      const nonFieldErrors = Object.values(result.data)
        .map((data) => getFieldResponseError(data, ['nonFieldErrors']))
        .filter((errorObject) => !!(errorObject ?? ''))

      if (!isNil(nonFieldErrors[0]) && !overrideNonFieldErrorHandler) {
        showErrorWithContinue(nonFieldErrors[0])
      }

      return result
    })
  })

  const errorHandlerLink = onError((error) =>
    globalErrorsHandler({
      error,
      setError,
      showDefaultError,
    })
  )

  const httpLink = new HttpLink(config)

  const client = new ApolloClient({
    link: from([
      nonFieldErrorHandlerLink,
      errorHandlerLink,
      // keep the terminating link at the end of the chain
      httpLink,
    ]),
    cache,
    assumeImmutableResults: true,
    // https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup
    name: 'web-flow',
    version: `v.${gitRevision}`,
  })

  return client
}
